import { formatCurrency } from '../utils/money'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

import Navbar from '../components/Navbar'
import Modal from '../components/Modal'
import PendingConfirmationsWidget from '../components/fulfillment/PendingConfirmationsWidget'
import { itemsApi, rentalsApi } from '../lib/api'
import { assetUrl } from '../utils/assetUrl'
import { hasPhysicalAddress } from '../utils/locations'

interface Item {
  id: string
  title: string
  category: string
  dailyPrice: number
  addressApprox: string
  isAvailableForRent: boolean
  totalRentals?: number
  pendingRequestCount?: number
  currentRentalId?: string | null
  isCurrentlyRented?: boolean
  photos?: Array<{ url: string; isDefault?: boolean }>
}

interface DashboardRental {
  id: string
  status: string
  isPaid: boolean
  startDate: string
  endDate: string
  totalAmount: number
  item: {
    id: string
    title: string
    photos?: Array<{ url: string }>
    dailyPrice: number
  }
  owner: {
    id: string
    firstName: string
    lastName: string
  }
}

interface DashboardStats {
  activeListings: number
  activeRentals: number
  totalEarnings: number
}

const RENTAL_PAGE_SIZE = 5

const rentalStatusLabels: Record<string, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Approved',
  ACTIVE: 'Active',
  RETURNED: 'Return reported',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  DISPUTED: 'Disputed',
}

const rentalStatusClasses: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  RETURNED: 'bg-slate-100 text-slate-800',
  COMPLETED: 'bg-slate-100 text-slate-800',
  CANCELLED: 'bg-red-100 text-red-800',
  DISPUTED: 'bg-red-100 text-red-800',
}

export default function UserDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState<Item[]>([])
  const [rentals, setRentals] = useState<DashboardRental[]>([])
  const [rentalPage, setRentalPage] = useState(1)
  const [rentalTotalPages, setRentalTotalPages] = useState(1)
  const [stats, setStats] = useState<DashboardStats>({ activeListings: 0, activeRentals: 0, totalEarnings: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [rentalsLoading, setRentalsLoading] = useState(false)
  const [itemsError, setItemsError] = useState<string | null>(null)
  const [statsError, setStatsError] = useState<string | null>(null)
  const [rentalsError, setRentalsError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Item | null>(null)
  const [deleteError, setDeleteError] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  useEffect(() => {
    fetchRentalsPage(rentalPage)
  }, [rentalPage])

  const fetchDashboardData = async () => {
    setIsLoading(true)
    setItemsError(null)
    setStatsError(null)

    const [itemsResult, statsResult] = await Promise.allSettled([
      itemsApi.getMyItems(),
      rentalsApi.getDashboardStats(),
    ])

    if (itemsResult.status === 'fulfilled') {
      const itemsResponse = itemsResult.value
      setItems(itemsResponse.data.data || itemsResponse.data.items || [])
    } else {
      const err = itemsResult.reason as any
      console.error('Error fetching dashboard items:', err)
      setItemsError(err?.response?.data?.error || 'Could not load your items. Please try again.')
    }

    if (statsResult.status === 'fulfilled') {
      const statsResponse = statsResult.value
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data)
      } else {
        setStatsError(statsResponse.data.error || 'Could not load dashboard stats.')
      }
    } else {
      const err = statsResult.reason as any
      console.error('Error fetching dashboard stats:', err)
      setStatsError(err?.response?.data?.error || 'Could not load dashboard stats. Please try again.')
    }

    setIsLoading(false)
  }

  const fetchRentalsPage = async (page: number) => {
    setRentalsLoading(true)
    setRentalsError(null)
    try {
      const response = await rentalsApi.getAll({
        role: 'renter',
        status: 'PENDING,APPROVED,ACTIVE,RETURNED,DISPUTED',
        page,
        limit: RENTAL_PAGE_SIZE,
      })
      if (response.data.success) {
        setRentals(response.data.data || [])
        const total = response.data.meta?.total || 0
        setRentalTotalPages(Math.max(1, Math.ceil(total / RENTAL_PAGE_SIZE)))
      } else {
        setRentalsError(response.data.error || 'Could not load your rentals.')
      }
    } catch (err: any) {
      console.error('Error fetching dashboard rentals:', err)
      setRentalsError(err?.response?.data?.error || 'Could not load your rentals. Please try again.')
    } finally {
      setRentalsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    setDeleteError('')
    try {
      await itemsApi.delete(deleteTarget.id)
      setDeleteTarget(null)
      fetchDashboardData()
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error || 'Failed to delete listing')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />

      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-6">
          {(() => {
            const missing: string[] = []
            if (!hasPhysicalAddress(user?.metadata?.locations?.pickup as any)) missing.push('a pickup address to list items for rent')
            if (!hasPhysicalAddress(user?.metadata?.locations?.['drop-off'] as any)) missing.push('a shipping address to rent items')
            return missing.length > 0 ? (
              <div role="alert" className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                <span aria-hidden="true">&#9888;</span>
                <span>
                  You still need {missing.join(' and ')}. Use a real street address (street, city, state and ZIP; no PO boxes).{' '}
                  <Link to="/profile?tab=locations" className="font-semibold underline">Add an address</Link>
                </span>
              </div>
            ) : null
          })()}

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">My Dashboard</h1>
            <p className="text-[var(--text-secondary)] mt-1">Welcome back, {user?.firstName || user?.name || 'there'}!</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <Link to="/list-item" className="bg-white rounded-xl p-6 border border-[var(--border)] shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-[var(--bg-tint)] flex items-center justify-center mb-3">
                <i className="mdi mdi-hammer-screwdriver text-xl text-[var(--forest-green)]"></i>
              </div>
              <div className="text-3xl font-bold text-[var(--forest-green)] mb-1">{stats.activeListings}</div>
              <div className="text-sm text-[var(--text-secondary)] font-medium">My Listings</div>
            </Link>

            <Link to="/my-rentals" className="bg-white rounded-xl p-6 border border-[var(--border)] shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                <i className="mdi mdi-calendar-check text-xl text-blue-600"></i>
              </div>
              <div className="text-3xl font-bold text-blue-600 mb-1">{stats.activeRentals}</div>
              <div className="text-sm text-[var(--text-secondary)] font-medium">My Rentals</div>
            </Link>

            <Link to="/earnings" className="bg-white rounded-xl p-6 border border-[var(--border)] shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
                <i className="mdi mdi-currency-usd text-xl text-amber-600"></i>
              </div>
              <div className="text-3xl font-bold text-amber-600 mb-1">
                ${stats.totalEarnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-sm text-[var(--text-secondary)] font-medium">Total Earnings</div>
            </Link>
          </div>

          {(itemsError || statsError || rentalsError) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-xl mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="font-semibold">Couldn't load dashboard data</p>
                <p className="text-sm">{itemsError || statsError || rentalsError}</p>
              </div>
              <button
                onClick={() => { fetchDashboardData(); fetchRentalsPage(rentalPage); }}
                className="btn btn-primary shrink-0"
                disabled={isLoading || rentalsLoading}
              >
                {isLoading || rentalsLoading ? 'Retrying...' : 'Try again'}
              </button>
            </div>
          )}

          {/* Pending Confirmations Widget */}
          <PendingConfirmationsWidget />

          <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden mb-6">
            <div className="p-6 border-b border-[var(--border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-bold text-[var(--text-primary)]">My Items for Rent</h2>
              <Link to="/list-item" className="btn btn-primary self-start">
                <i className="mdi mdi-plus md:hidden"></i>
                <span className="hidden md:inline"><i className="mdi mdi-plus"></i> Add New Item</span>
              </Link>
            </div>

            {isLoading ? (
              <div className="p-8 text-center text-[var(--text-secondary)]">Loading...</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <i className="mdi mdi-items text-6xl text-[var(--border)] mb-4"></i>
                <p className="text-[var(--text-secondary)] mb-4">You haven't listed any items yet.</p>
                <Link to="/list-item" className="btn btn-primary">
                  List Your First Item
                </Link>
              </div>
            ) : (
              <div className="max-h-[640px] overflow-y-auto divide-y divide-[var(--border)]">
                {items.slice(0, 12).map((item) => {
                  // A click lands on the specific rental currently occupying this item
                  // (or the sole unambiguous pending request), falling back to the
                  // listing's edit page when there's no single rental to jump to --
                  // e.g. never rented, or multiple pending requests at once.
                  const itemHref = item.currentRentalId ? `/rental/${item.currentRentalId}` : `/edit-item/${item.id}`
                  return (
                  <div
                    key={item.id}
                    className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-[var(--bg-tint)] transition-colors duration-200 group"
                  >
                    <Link to={itemHref} className="flex items-center gap-4 min-w-0">
                      {item.photos && item.photos.length > 0 ? (
                        <img
                          src={assetUrl(item.photos.find((p) => p.isDefault)?.url || item.photos[0].url)}
                          alt={item.title}
                          className="w-14 h-14 rounded-lg object-cover shrink-0"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-14 h-14 bg-[var(--bg-tint)] rounded-lg flex items-center justify-center shrink-0">
                          <i className="mdi mdi-hammer-screwdriver text-xl text-[var(--forest-green)]"></i>
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-[var(--text-primary)] truncate group-hover:underline">{item.title}</div>
                        <div className="text-sm text-[var(--text-secondary)] truncate">{item.category} • {item.addressApprox}</div>
                        <div className="text-[var(--forest-green)] font-bold">${item.dailyPrice}/day</div>
                        <div className="text-xs text-[var(--text-muted)]">{item.totalRentals || 0} rentals on igotthatus.com</div>
                      </div>
                    </Link>

                    <div className="flex items-center gap-2 flex-wrap shrink-0 sm:justify-end">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                          item.isAvailableForRent
                            ? 'bg-[var(--bg-tint)] text-[var(--forest-green)] group-hover:bg-[var(--forest-green)] group-hover:text-white'
                            : 'bg-[var(--bg-tint)] text-[var(--text-secondary)]'
                        }`}
                      >
                        {item.isAvailableForRent ? 'Available' : 'Rented'}
                      </span>
                      {item.pendingRequestCount && item.pendingRequestCount > 0 ? (
                        <Link
                          to="/my-rentals"
                          state={{ tab: 'owner' }}
                          className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                          title={`${item.pendingRequestCount} pending request${item.pendingRequestCount !== 1 ? 's' : ''}`}
                        >
                          {item.pendingRequestCount} pending
                        </Link>
                      ) : null}
                      {item.isCurrentlyRented ? (
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600" title="This item is out with a renter, so it can't be edited or deleted until the rental is completed.">
                          Currently rented
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() => navigate(`/edit-item/${item.id}`)}
                            className="p-2 hover:bg-[var(--bg-tint)] rounded-lg"
                            title="Edit listing"
                          >
                            <i className="mdi mdi-pencil text-[var(--text-secondary)]"></i>
                          </button>
                          <button
                            onClick={() => setDeleteTarget(item)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                            title="Delete listing"
                          >
                            <i className="mdi mdi-trash-can text-red-500"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden">
            <div className="p-6 border-b border-[var(--border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-bold text-[var(--text-primary)]">Items I am Renting</h2>
              <Link to="/my-rentals" className="text-[var(--forest-green)] font-medium hover:underline">
                View all
              </Link>
            </div>

            {rentalsLoading ? (
              <div className="p-8 text-center text-[var(--text-secondary)]">Loading rentals...</div>
            ) : rentals.length === 0 ? (
              <div className="p-8 text-center">
                <i className="mdi mdi-calendar-blank text-6xl text-[var(--border)] mb-4"></i>
                <p className="text-[var(--text-secondary)] mb-4">You aren't renting any items right now.</p>
                <Link to="/browse" className="btn btn-primary">
                  Browse Items
                </Link>
              </div>
            ) : (
              <div className="max-h-[640px] overflow-y-auto divide-y divide-[var(--border)]">
                {rentals.map((rental) => (
                  <Link
                    key={rental.id}
                    to={`/rental/${rental.id}`}
                    className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 hover:bg-[var(--bg-tint)] transition-colors duration-200 group"
                  >
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
                      <div className="w-16 h-16 sm:w-14 sm:h-14 rounded-lg bg-[var(--bg-tint)] flex items-center justify-center shrink-0 overflow-hidden">
                        {rental.item.photos && rental.item.photos.length > 0 ? (
                          <img
                            src={assetUrl(rental.item.photos[0].url)}
                            alt={rental.item.title}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <i className="mdi mdi-hammer-screwdriver text-2xl sm:text-xl text-[var(--forest-green)]"></i>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[var(--text-primary)] text-base leading-tight mb-1">
                          <span className="line-clamp-2 sm:line-clamp-1">{rental.item.title}</span>
                        </div>
                        <div className="text-sm text-[var(--text-secondary)] mb-1">
                          {new Date(rental.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {' '}
                          {new Date(rental.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="text-[var(--forest-green)] font-bold text-sm">{formatCurrency(rental.totalAmount)} total</div>
                      </div>
                    </div>

                    <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-1.5 shrink-0">
                      <span
                        className={`px-3 py-1.5 sm:py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                          rentalStatusClasses[rental.status] || 'bg-slate-100 text-slate-800'
                        }`}
                      >
                        {rentalStatusLabels[rental.status] || rental.status}
                      </span>
                      <span
                        className={`px-3 py-1.5 sm:py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                          rental.isPaid ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {rental.isPaid ? 'Paid' : 'Payment pending'}
                      </span>
                      <i className="mdi mdi-chevron-right text-[var(--text-muted)] hidden sm:block"></i>
                    </div>
                  </Link>
                ))}

                {rentalTotalPages > 1 && (
                  <div className="p-4 flex items-center justify-between">
                    <button
                      onClick={() => setRentalPage((p) => Math.max(1, p - 1))}
                      disabled={rentalPage <= 1}
                      className="px-3 py-1.5 rounded-lg border border-[var(--border)] text-sm disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-[var(--text-secondary)]">Page {rentalPage} of {rentalTotalPages}</span>
                    <button
                      onClick={() => setRentalPage((p) => Math.min(rentalTotalPages, p + 1))}
                      disabled={rentalPage >= rentalTotalPages}
                      className="px-3 py-1.5 rounded-lg border border-[var(--border)] text-sm disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom breathing room: App.css's .container { padding: 0 2rem } overrides py-6, so use a real element. */}
          <div aria-hidden="true" className="h-10" />
        </div>
      </main>

      <Modal
        isOpen={!!deleteTarget}
        title="Delete listing"
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteTarget(null)
            setDeleteError('')
          }
        }}
        confirmVariant="danger"
      >
        <div>
          {deleteError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{deleteError}</div>
          ) : null}
          <p>
            Are you sure you want to delete <span className="font-semibold text-[var(--text-primary)]">{deleteTarget?.title}</span>?
            This cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  )
}
